<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy"
                @submit.prevent="storeType">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>New Asset Type</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col v-if="user.type==='admin'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']" :items="companies"
                                      density="compact" label="Company">
                            </company-select>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['name']" autofocus
                                          :rules="[vRules.required]" density="compact" label="Name"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-img :src="image" width="200"/>
                            <v-file-input v-model="formData['file']" :rules="[vRules.required]" accept="image/*"
                                          density="compact" label="Select Image" @change="imagePreview"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['font_text']"
                                          :rules="[vRules.required]" density="compact" label="Font Text"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['font_size']" :rules="[vRules.required,vRules.gte(8)]"
                                          density="compact"
                                          label="Font Size (px)" min="8"
                                          type="number"/>
                        </v-col>
                        <v-col class="py-0 text-capitalize" cols="12" sm="6">
                            <v-select v-model="formData['category']"
                                      :items="assetCategories"
                                      :rules="[vRules.required]"
                                      density="compact"
                                      label="Category">
                            </v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-color-picker v-model="formData['font_color']" :rules="[vRules.required]"
                                            density="compact" hide-inputs label="Font Color"/>
                        </v-col>

                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {assetCategories} from "@/composables/categoryConfig";
import CompanySelect from "@/components/globals/company-select.vue";

const companies = ref([]);
const {user} = useAuthStore();
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    duplicateFrom: {type: Object, default: null},
});

const formData = reactive({
    valid: false,
    name: null,
    file: null,
    category: null,
    font_text: null,
    font_size: null,
    font_color: null,
    photo_url: null,
    company_id: ((user.type !== 'admin') ? user.company.id : null),
});
const image = ref(null);
const emits = defineEmits(['update:modelValue', 'addType']);
const dialogSwitch = ref(props.modelValue);
const errors = ref([]);

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompanies();
    }
    if (props.duplicateFrom) {
        const s = props.duplicateFrom;
        if (s.category) formData['category'] = s.category;
        if (s.font_text) formData['font_text'] = s.font_text;
        if (s.font_size) formData['font_size'] = s.font_size;
        if (s.font_color) formData['font_color'] = s.font_color;
        if (s.company_id && user.type === 'admin') formData['company_id'] = s.company_id;
        // name and file left empty - user must provide unique values
    }
})
const imagePreview = () => {
    formData['file'] = formData['file'];
    image.value = URL.createObjectURL(formData['file']);
}

const fetchCompanies = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Companies could not be loaded', 'error')
        })
}
const storeType = () => {
    errors.value = [];
    send('post', `asset-types`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addType', {
                    'status': 'enabled',
                    ...response.data.data.asset_type,
                    company_name: response.data.data.asset_type.company?.company_name ?? ''
                });
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
